import type { RouteDef } from '../router';
import { getTopTourism, allCountries } from '../data/prices';
import { countryDisplayName } from '../utils/country-name';
import { t, getLocale } from '../i18n';
import { getPayload, exposeVariant, onExperimentsLoaded } from '../experiments';
import { homePageHtml, homeRotation, rateFloor, tickerItemsHtml, type HomeCountry } from '@roamzy/shared';

/** Every priced country with its name in the page language. */
function homeCountries(): HomeCountry[] {
  return allCountries.map((c) => ({ ...c, name: countryDisplayName(c) }));
}

const topSlugs = (): string[] => getTopTourism().map((c) => c.slug);

function shuffled<T>(xs: readonly T[]): T[] {
  const pool = [...xs];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = pool[i]!; const b = pool[j]!;
    pool[i] = b; pool[j] = a;
  }
  return pool;
}

const home: RouteDef = {
  path: '/',
  routeType: 'home',
  navKey: 'home',
  metaTitle: 'Roamzy — One eSIM. The whole world. Pay per megabyte.',
  metaDesc: 'No packages, no expiring data. Top up once, travel anywhere, pay only for what you use.',

  // The server sends this page (backend home-body.ts, the same renderer);
  // the router keeps it on first paint, so the price painted with the first
  // HTML byte is not redrawn.
  prerenderMarker: '.home-route',
  hydrate: () => '',

  render() {
    return `<div class="home-route">${homePageHtml({
      locale: getLocale(),
      t: (key, vars) => t(key as Parameters<typeof t>[0], vars),
      countries: homeCountries(),
      topSlugs: topSlugs(),
    })}</div>`;
  },

  mount(root) {
    const numEl = root.querySelector<HTMLElement>('[data-hero-number]');
    const rateEl = root.querySelector<HTMLElement>('[data-hero-rate]');
    const flagEl = root.querySelector<HTMLElement>('[data-hero-flag]');
    const countryEl = root.querySelector<HTMLElement>('[data-hero-country]');

    // The ticker arrives as the server printed it, tourism first. Reshuffle
    // it so each visit shows different countries (operator 2026-05-21: "the
    // phone always starts with Spain, Italy"): every regular country on
    // desktop, 30 on phones. The CSS loop scrolls the track by half its
    // width, so the items go in twice.
    try {
      const track = root.querySelector<HTMLElement>('.ticker__track');
      if (track) {
        const pool = shuffled(homeCountries().filter((c) => !c.isPremium));
        const rows = matchMedia('(max-width: 768px)').matches ? pool.slice(0, 30) : pool;
        const items = tickerItemsHtml(rows, getLocale());
        track.innerHTML = `${items}<span class="ticker__sep">·</span>${items}`;
      }
    } catch {
      // The ticker is decoration; never let it abort mount.
    }

    // Mobile-only: swap the comparison line for the short variant.
    // The full string ("from {floor_gb}/GB · vs $4–8/GB on typical travel
    // data packs") wraps to a second uneven line on 360-400px screens;
    // the short variant ("from {floor_gb}/GB · vs $4–8/GB") fits on one
    // clean line. Done as a JS DOM swap (not HTML span hide) to avoid
    // mixing CSS-toggle classes inside JSON i18n strings — that pattern
    // bit us 2026-05-08. Try/catch is defensive: a missing key falls
    // through to the original full string, never crashes mount.
    try {
      if (matchMedia('(max-width: 768px)').matches) {
        const compareEl = root.querySelector<HTMLElement>('[data-hero-compare]');
        if (compareEl) {
          const short = t('home.heroExtra.compareShort', { floorGb: rateFloor(homeCountries()).floorGb });
          // t() returns the key string itself when missing — guard
          // against that so we never overwrite the full string with
          // a literal "home.heroExtra.compareShort" placeholder.
          if (short && short !== 'home.heroExtra.compareShort') {
            compareEl.innerHTML = short;
          }
        }
      }
    } catch {
      // Mobile compare-swap is purely cosmetic; never let it abort mount.
    }

    if (!numEl || !rateEl || !countryEl || !flagEl) return;

    // ── A/B test: home_hero_v1 ──
    // Variant payload shape (see admin → Маркетинг → A/B):
    //   { headline?: string, subhead?: string, cta?: string }
    // Apply when assignments arrive; falls through to t() defaults otherwise.
    const applyHeroVariant = (): void => {
      const p = getPayload<{ headline?: string; subhead?: string; cta?: string }>('home_hero_v1', {});
      if (!p || (!p.headline && !p.subhead && !p.cta)) return;
      if (p.headline) {
        const tagline = root.querySelector<HTMLElement>('[data-hero-tagline]');
        if (tagline) tagline.textContent = p.headline;
      }
      if (p.subhead) {
        const lede = root.querySelector<HTMLElement>('[data-hero-lede]');
        if (lede) lede.textContent = p.subhead;
      }
      if (p.cta) {
        const ctaA = root.querySelector<HTMLAnchorElement>('[data-hero-cta] .btn');
        if (ctaA) ctaA.textContent = p.cta;
      }
      exposeVariant('home_hero_v1');
    };
    // Try once now (cache may already be primed if we got here after boot).
    applyHeroVariant();
    // Also subscribe in case boot hasn't completed yet.
    onExperimentsLoaded(() => { applyHeroVariant(); });

    // The rotation the server started: its first country is the one printed.
    const rotation = homeRotation(homeCountries(), topSlugs());
    if (rotation.length === 0) return;

    // Respect prefers-reduced-motion — but DON'T fully disable the
    // rotation. Earlier this returned early, which meant iOS PWA users
    // (Low Power Mode auto-enables reduce-motion system-wide, and PWAs
    // inherit it stricter than Safari tabs) never saw country variety.
    // Operator caught this 2026-05-21: «в PWA страны не меняются как
    // на web».
    //
    // Soft-degrade: when reduce-motion is set, do a slower interval
    // (6s vs 2.5s) with INSTANT text swap (no opacity fade) so users
    // still see different countries scroll through without any
    // vestibular-triggering motion. The accompanying CSS in
    // fix-visual-a11y.css also slows the top ticker to 90s/loop in the
    // same mode so both surfaces degrade consistently.
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ROTATION_MS = reducedMotion ? 6000 : 2500;
    const FADE_OUT_MS = reducedMotion ? 0 : 380;

    if (!reducedMotion) {
      numEl.style.transition = 'opacity .35s var(--ease, ease)';
      // Promote the rotating number to its own compositor layer so each
      // text-swap doesn't trigger a main-thread repaint of the giant
      // italic display font. Cleared in cleanup.
      numEl.style.willChange = 'opacity';
    } else {
      // reduce-motion path: no compositor promotion (browser may have
      // throttled animations anyway) and no opacity transition. The
      // text just changes in place every 6 seconds.
      numEl.style.transition = 'none';
    }

    let i = 0;
    const tick = (): void => {
      i = (i + 1) % rotation.length;
      const c = rotation[i]!;
      if (reducedMotion) {
        // Instant swap — no fade-out / fade-in dance.
        rateEl.textContent = c.rateUsdtPerMb.toFixed(4);
        flagEl.textContent = c.flag ?? '';
        countryEl.textContent = c.name;
        return;
      }
      numEl.style.opacity = '0';
      // 380ms (not 350) gives a 30ms safety margin past the CSS .35s
      // transition so a slow mobile GPU has finished painting opacity:0
      // before we swap the text. Without this margin the new text can
      // briefly overlap the old mid-fade ("выглядит так себе" — operator
      // 2026-05-08).
      window.setTimeout(() => {
        rateEl.textContent = c.rateUsdtPerMb.toFixed(4);
        flagEl.textContent = c.flag ?? '';
        countryEl.textContent = c.name;
        numEl.style.opacity = '1';
      }, FADE_OUT_MS);
    };

    let id = window.setInterval(tick, ROTATION_MS);

    // Pause rotation when the tab is hidden, resume on focus.
    const onVis = (): void => {
      if (document.hidden) {
        window.clearInterval(id);
      } else {
        window.clearInterval(id);
        id = window.setInterval(tick, ROTATION_MS);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.clearInterval(id);
      // Release the compositor layer — leaving will-change on after the
      // animation stops bloats memory.
      numEl.style.willChange = 'auto';
      document.removeEventListener('visibilitychange', onVis);
    };
  },
};

export default home;
